import { useMemo, useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { Checkbox } from './ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { mdiArrowLeft, mdiArrowRight, mdiCheck, mdiClose, mdiDragVertical, mdiLoading, mdiPencil, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { useCan } from '../contexts/PermissionsContext'
import {
  usePropertyMappingRule,
  useUpdatePropertyMappingRule,
  useDeletePropertyMappingRule,
  useCreatePropertyMappingRuleEntry,
  useUpdatePropertyMappingRuleEntry,
  useDeletePropertyMappingRuleEntry,
  type MappingRuleTarget,
  type PropertyMappingRuleEntry,
} from '../hooks/usePropertyMappingRulesApi'
import { usePropertyDefinitions } from '../hooks/useQueries'
import { EmptyState } from './ui/empty-state'

// conditionText prints a condition value as typed.
function conditionText(value: unknown): string {
  if (value === undefined || value === null) return ''
  return typeof value === 'string' ? value : JSON.stringify(value)
}

function getLocalizedName(name: string | Record<string, string> | undefined, locale: string): string {
  if (!name) return 'Unnamed'
  if (typeof name === 'string') return name
  return name[locale] || name.en || name.fr || Object.values(name)[0] || 'Unnamed'
}

export function PropertyMappingRuleDetailPage() {
  const { org, uid } = useParams({ strict: false }) as { org: string; uid: string }
  const { locale } = useLocale()
  const navigate = useNavigate()
  const { t } = useTranslation('properties')
  // Mapping rules are type-system configuration: every mutation here needs
  // `configuration.write`, while the rule stays readable (spec 2026-09-04-02).
  const canWriteConfiguration = useCan('configuration.write')

  const { data: rule, isLoading, error } = usePropertyMappingRule(org, uid)
  const updateMutation = useUpdatePropertyMappingRule(org, uid)
  const deleteMutation = useDeletePropertyMappingRule(org)

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [editingCode, setEditingCode] = useState(false)
  const [codeValue, setCodeValue] = useState('')

  // Entry dialog state
  const [entryDialogOpen, setEntryDialogOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<PropertyMappingRuleEntry | null>(null)
  const [entryRole, setEntryRole] = useState<string>('value')
  const [entrySourceCode, setEntrySourceCode] = useState('')
  const [entryTargetCode, setEntryTargetCode] = useState('')
  const [entryTimelined, setEntryTimelined] = useState(false)
  const [entryWhenCode, setEntryWhenCode] = useState('')
  const [entryWhenEquals, setEntryWhenEquals] = useState('')
  const [deleteEntryUid, setDeleteEntryUid] = useState<string | null>(null)

  // Every property definition, for the source/target pickers. This used to
  // be a bespoke `apiClient.GET(…, { limit: 1000 })`: the server clamped it
  // (and, before this spec, silently fell back to its default of 20), so the
  // pickers offered 20 of 653 definitions, ordered by code — everything
  // sorting after roughly `asset_` was simply unreachable, with no error and
  // no warning. `usePropertyDefinitions` pages the catalogue to exhaustion.
  const { data: allProperties } = usePropertyDefinitions(org)

  useEffect(() => {
    if (rule) {
      setCodeValue(rule.code || '')
    }
  }, [rule])

  const createEntryMutation = useCreatePropertyMappingRuleEntry(org, uid)
  const updateEntryMutation = useUpdatePropertyMappingRuleEntry(org, uid)
  const deleteEntryMutation = useDeletePropertyMappingRuleEntry(org, uid)

  const docTypeName = rule ? getLocalizedName(rule.document_type?.name, locale) : ''
  const assetTypeName = rule ? getLocalizedName(rule.asset_type?.name, locale) : ''
  const title = rule ? `${docTypeName} -> ${assetTypeName}` : t('mapping_rule_detail.loading')

  useBreadcrumbs(useMemo(() => [
    { title: t('mapping_rules.breadcrumb_home'), path: `/${org}` },
    { title: t('mapping_rules.breadcrumb_settings'), path: `/${org}/settings` },
    { title: t('mapping_rules.breadcrumb_mappings'), path: `/${org}/property-mapping-rules` },
    { title: rule?.code || title }
  ], [org, rule, title, t]))

  const handleCodeSave = () => {
    updateMutation.mutate({ code: codeValue || undefined })
    setEditingCode(false)
  }

  const handleToggleEnabled = (checked: boolean) => {
    updateMutation.mutate({ enabled: checked })
  }

  const handleTargetChange = (target: string) => {
    updateMutation.mutate({ target: target as MappingRuleTarget })
  }

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(uid)
    navigate({ to: '/$org/property-mapping-rules', params: { org } })
  }

  const openAddEntryDialog = () => {
    setEditingEntry(null)
    setEntryRole('value')
    setEntrySourceCode('')
    setEntryTargetCode('')
    setEntryTimelined(false)
    setEntryWhenCode('')
    setEntryWhenEquals('')
    setEntryDialogOpen(true)
  }

  const openEditEntryDialog = (entry: PropertyMappingRuleEntry) => {
    setEditingEntry(entry)
    setEntryRole(entry.role)
    setEntrySourceCode(entry.source_property?.code || '')
    setEntryTargetCode(entry.target_property?.code || '')
    setEntryTimelined(entry.timelined)
    setEntryWhenCode(entry.when_property_code || '')
    setEntryWhenEquals(conditionText(entry.when_equals))
    setEntryDialogOpen(true)
  }

  // An empty condition property clears the condition; the value is sent as
  // typed (a string), which is what enum and text properties hold.
  const conditionBody = () => entryWhenCode
    ? { when_property_code: entryWhenCode, when_equals: entryWhenEquals.trim() }
    : { when_property_code: '' }

  const handleEntrySave = () => {
    if (editingEntry) {
      updateEntryMutation.mutate({
        entryUid: editingEntry.uid,
        source_property_code: entrySourceCode,
        target_property_code: entryRole === 'value' ? entryTargetCode : undefined,
        timelined: entryRole === 'value' ? entryTimelined : undefined,
        ...conditionBody(),
      }, {
        onSuccess: () => setEntryDialogOpen(false),
      })
    } else {
      createEntryMutation.mutate({
        source_property_code: entrySourceCode,
        target_property_code: entryRole === 'value' ? entryTargetCode : undefined,
        role: entryRole,
        timelined: entryRole === 'value' ? entryTimelined : undefined,
        ...(entryWhenCode ? conditionBody() : {}),
      }, {
        onSuccess: () => setEntryDialogOpen(false),
      })
    }
  }

  const handleEntryDelete = () => {
    if (!deleteEntryUid) return
    deleteEntryMutation.mutate(deleteEntryUid, {
      onSuccess: () => setDeleteEntryUid(null),
    })
  }

  const handleTimelinedToggle = (entry: PropertyMappingRuleEntry, checked: boolean) => {
    updateEntryMutation.mutate({
      entryUid: entry.uid,
      timelined: checked,
    })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
        </div>
      </div>
    )
  }

  if (error || !rule) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">{t('mapping_rule_detail.error')}</h1>
          <p className="text-muted-foreground">{(error as any)?.title || t('mapping_rule_detail.not_found')}</p>
          <Link to="/$org/property-mapping-rules" params={{ org }}>
            <Button className="mt-4"><AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />{t('mapping_rule_detail.back_to_rules')}</Button>
          </Link>
        </div>
      </div>
    )
  }

  const entries = rule.entries || []
  const valueEntries = entries.filter(e => e.role === 'value')
  const dateEntries = entries.filter(e => e.role === 'start_date' || e.role === 'end_date')

  // Check if date roles already exist (for warnings)
  const hasStartDate = dateEntries.some(e => e.role === 'start_date')
  const hasEndDate = dateEntries.some(e => e.role === 'end_date')

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{title}</h1>
              {rule.code ? (
                <p className="text-sm text-muted-foreground font-mono">{rule.code}</p>
              ) : (
                <p className="text-sm text-muted-foreground">{t('mapping_rule_detail.no_code')}</p>
              )}
            </div>
          </div>
          {canWriteConfiguration && (
            <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)} data-testid="delete-rule-btn">
              <AppIcon path={mdiTrashCanOutline} size="sm" className="mr-2" />{t('mapping_rule_detail.delete_button')}
            </Button>
          )}
        </div>

        {/* Metadata Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('mapping_rule_detail.rule_details_title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">{t('mapping_rule_detail.code_label')}</Label>
                {editingCode && canWriteConfiguration ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={codeValue}
                      onChange={(e) => setCodeValue(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                      className="h-8"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCodeSave()
                        if (e.key === 'Escape') setEditingCode(false)
                      }}
                    />
                    <Button size="sm" variant="ghost" onClick={handleCodeSave}><AppIcon path={mdiCheck} size="sm" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingCode(false)}><AppIcon path={mdiClose} size="sm" /></Button>
                  </div>
                ) : (
                  <p
                    className={`text-sm${canWriteConfiguration ? ' cursor-pointer hover:text-primary' : ''}`}
                    onClick={canWriteConfiguration ? () => setEditingCode(true) : undefined}
                  >
                    {rule.code || <span className="text-muted-foreground">{canWriteConfiguration ? t('mapping_rule_detail.no_code_click_to_edit') : t('mapping_rule_detail.no_code')}</span>}
                    {canWriteConfiguration && <AppIcon path={mdiPencil} size="xs" className="inline ml-1" />}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">{t('mapping_rule_detail.created_label')}</Label>
                <p className="text-sm">{rule.created_at ? new Date(rule.created_at).toLocaleString() : '-'}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">{t('mapping_rule_detail.document_type_label')}</Label>
                <p className="text-sm">{docTypeName}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">{t('mapping_rule_detail.asset_type_label')}</Label>
                <p className="text-sm">

                  {assetTypeName}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {/* The checkbox is the enabled indicator too: it stays on
                    screen, read-only, rather than disappearing. */}
                <Checkbox
                  checked={rule.enabled}
                  disabled={!canWriteConfiguration}
                  onCheckedChange={(checked) => handleToggleEnabled(checked === true)}
                  data-testid="enabled-checkbox"
                />
                <Label>{t('mapping_rule_detail.enabled_label')}</Label>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">{t('mapping_rule_detail.target_label')}</Label>
                <Select
                  value={rule.target || 'subject_or_ancestor'}
                  onValueChange={handleTargetChange}
                  disabled={!canWriteConfiguration}
                >
                  <SelectTrigger data-testid="rule-target-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subject_or_ancestor">{t('mapping_rule_detail.target_subject_or_ancestor')}</SelectItem>
                    <SelectItem value="subject_only">{t('mapping_rule_detail.target_subject_only')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('mapping_rule_detail.target_help')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Entries Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('mapping_rule_detail.entries_title', { count: entries.length })}</CardTitle>
              {canWriteConfiguration && (
                <Button size="sm" onClick={openAddEntryDialog} data-testid="add-entry-btn">
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />{t('mapping_rule_detail.add_entry_button')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <EmptyState variant="inline" title={t('mapping_rule_detail.no_entries')} />
            ) : (
              <div className="space-y-4">
                {/* Value Mappings */}
                {valueEntries.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">{t('mapping_rule_detail.value_mappings_title')}</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>{t('mapping_rule_detail.column_source_property')}</TableHead>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>{t('mapping_rule_detail.column_target_property')}</TableHead>
                          <TableHead>{t('mapping_rule_detail.column_timelined')}</TableHead>
                          <TableHead className="w-20">{t('mapping_rule_detail.column_actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {valueEntries.map((entry) => (
                          <TableRow key={entry.uid}>
                            <TableCell><AppIcon path={mdiDragVertical} size="sm" tone="muted" /></TableCell>
                            <TableCell>
                              {getLocalizedName(entry.source_property?.name, locale)}
                              {entry.source_property?.code && (
                                <code className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded">{entry.source_property.code}</code>
                              )}
                              {entry.when_property_code && (
                                <div className="mt-1" data-testid={`entry-condition-${entry.uid}`}>
                                  <Badge variant="outline">
                                    {t('mapping_rule_detail.condition_badge', { property: entry.when_property_code, value: conditionText(entry.when_equals) })}
                                  </Badge>
                                </div>
                              )}
                            </TableCell>
                            <TableCell><AppIcon path={mdiArrowRight} size="sm" /></TableCell>
                            <TableCell>
                              {getLocalizedName(entry.target_property?.name, locale)}
                              {entry.target_property?.code && (
                                <code className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded">{entry.target_property.code}</code>
                              )}
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                checked={entry.timelined}
                                disabled={!canWriteConfiguration}
                                onCheckedChange={(checked) => handleTimelinedToggle(entry, checked === true)}
                                data-testid={`entry-timelined-${entry.uid}`}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {canWriteConfiguration && (
                                  <>
                                    <Button variant="ghost" size="sm" onClick={() => openEditEntryDialog(entry)} data-testid={`entry-edit-${entry.uid}`}>
                                      <AppIcon path={mdiPencil} size="xs" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setDeleteEntryUid(entry.uid)} data-testid={`entry-delete-${entry.uid}`}>
                                      <AppIcon path={mdiTrashCanOutline} size="xs" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Date Roles */}
                {dateEntries.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">{t('mapping_rule_detail.date_roles_title')}</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('mapping_rule_detail.column_role')}</TableHead>
                          <TableHead>{t('mapping_rule_detail.column_source_property')}</TableHead>
                          <TableHead>{t('mapping_rule_detail.column_target')}</TableHead>
                          <TableHead className="w-20">{t('mapping_rule_detail.column_actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dateEntries.map((entry) => (
                          <TableRow key={entry.uid} className="bg-muted/30">
                            <TableCell>
                              <Badge variant="outline">{entry.role}</Badge>
                            </TableCell>
                            <TableCell>
                              {getLocalizedName(entry.source_property?.name, locale)}
                              {entry.source_property?.code && (
                                <code className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded">{entry.source_property.code}</code>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">-</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {canWriteConfiguration && (
                                  <>
                                    <Button variant="ghost" size="sm" onClick={() => openEditEntryDialog(entry)} data-testid={`entry-edit-${entry.uid}`}>
                                      <AppIcon path={mdiPencil} size="xs" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setDeleteEntryUid(entry.uid)} data-testid={`entry-delete-${entry.uid}`}>
                                      <AppIcon path={mdiTrashCanOutline} size="xs" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Preview */}
        {entries.length > 0 && (
          <Card className="bg-muted/50">
            <CardHeader>
              <CardTitle className="text-sm">{t('mapping_rule_detail.preview_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                {t('mapping_rule_detail.preview_description', {
                  docType: docTypeName,
                  assetType: assetTypeName
                })}
              </p>
              <ul className="text-sm space-y-1 font-mono">
                {valueEntries.map((entry) => (
                  <li key={entry.uid} className="text-muted-foreground">
                    {entry.source_property?.code} -&gt; {entry.target_property?.code}
                    {entry.timelined && hasStartDate ? ` ${t('mapping_rule_detail.preview_timelined')}` : ` ${t('mapping_rule_detail.preview_plain')}`}
                  </li>
                ))}
                {dateEntries.length > 0 && (
                  <li className="text-muted-foreground mt-2">
                    {t('mapping_rule_detail.preview_date_range')} {dateEntries.map(e => `${e.source_property?.code} (${e.role.replace('_', ' ')})`).join(' / ')}
                  </li>
                )}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Entry Add/Edit Dialog */}
      <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEntry ? t('mapping_rule_detail.entry_dialog_edit_title') : t('mapping_rule_detail.entry_dialog_add_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 p-4">
            <div>
              <Label>{t('mapping_rule_detail.entry_role_label')}</Label>
              {editingEntry ? (
                <p className="text-sm font-medium">{editingEntry.role}</p>
              ) : (
                <Select value={entryRole} onValueChange={setEntryRole}>
                  <SelectTrigger data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="value">{t('mapping_rule_detail.entry_role_value')}</SelectItem>
                    <SelectItem value="start_date">{t('mapping_rule_detail.entry_role_start_date')}</SelectItem>
                    <SelectItem value="end_date">{t('mapping_rule_detail.entry_role_end_date')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {!editingEntry && entryRole === 'start_date' && hasStartDate && (
              <div className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded">
                {t('mapping_rule_detail.entry_start_date_exists')}
              </div>
            )}
            {!editingEntry && entryRole === 'end_date' && hasEndDate && (
              <div className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded">
                {t('mapping_rule_detail.entry_end_date_exists')}
              </div>
            )}

            <div>
              <Label>{t('mapping_rule_detail.entry_source_property_label')}</Label>
              <Select value={entrySourceCode} onValueChange={setEntrySourceCode}>
                <SelectTrigger data-testid="source-property-select">
                  <SelectValue placeholder={t('mapping_rule_detail.entry_source_property_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {allProperties.map((p) => (
                    <SelectItem key={p.uid} value={p.code}>
                      {getLocalizedName(p.name, locale)} ({p.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {entryRole === 'value' && (
              <>
                <div>
                  <Label>{t('mapping_rule_detail.entry_target_property_label')}</Label>
                  <Select value={entryTargetCode} onValueChange={setEntryTargetCode}>
                    <SelectTrigger data-testid="target-property-select">
                      <SelectValue placeholder={t('mapping_rule_detail.entry_target_property_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {allProperties.map((p) => (
                        <SelectItem key={p.uid} value={p.code}>
                          {getLocalizedName(p.name, locale)} ({p.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={entryTimelined}
                    onCheckedChange={(checked) => setEntryTimelined(checked === true)}
                    data-testid="timelined-checkbox"
                  />
                  <Label>{t('mapping_rule_detail.entry_timelined_label')}</Label>
                </div>
                <div>
                  <Label>{t('mapping_rule_detail.entry_condition_label')}</Label>
                  <Select value={entryWhenCode || '__none__'} onValueChange={(v) => setEntryWhenCode(v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="when-property-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('mapping_rule_detail.entry_condition_none')}</SelectItem>
                      {allProperties.map((p) => (
                        <SelectItem key={p.uid} value={p.code}>
                          {getLocalizedName(p.name, locale)} ({p.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {entryWhenCode && (
                    <Input
                      className="mt-2"
                      value={entryWhenEquals}
                      onChange={(e) => setEntryWhenEquals(e.target.value)}
                      placeholder={t('mapping_rule_detail.entry_condition_value_placeholder')}
                      data-testid="when-equals-input"
                    />
                  )}
                </div>
              </>
            )}

            {(createEntryMutation.isError || updateEntryMutation.isError) && (
              <div className="text-sm text-destructive">
                {(createEntryMutation.error as any)?.detail || (updateEntryMutation.error as any)?.detail || t('mapping_rule_detail.entry_save_failed')}
              </div>
            )}

            <Button
              onClick={handleEntrySave}
              disabled={createEntryMutation.isPending || updateEntryMutation.isPending || !entrySourceCode || (entryRole === 'value' && !entryTargetCode) || (!!entryWhenCode && !entryWhenEquals.trim())}
              data-testid="save-entry-btn"
              className="w-full"
            >
              {(createEntryMutation.isPending || updateEntryMutation.isPending) && <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />}
              {editingEntry ? t('mapping_rule_detail.entry_update_button') : t('mapping_rule_detail.entry_add_button')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Rule Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('mapping_rule_detail.delete_rule_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('mapping_rule_detail.delete_rule_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-btn">{t('common:actions.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Entry Dialog */}
      <AlertDialog open={!!deleteEntryUid} onOpenChange={(open) => !open && setDeleteEntryUid(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('mapping_rule_detail.delete_entry_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('mapping_rule_detail.delete_entry_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleEntryDelete}>{t('mapping_rule_detail.remove_button')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
